import { Checkbox } from "PFComponents/checkbox";
import { Inline, Stack } from "PFComponents/containers";
import { ColumnHelper, HeaderContext, Row } from "PFComponents/table/table.types";

import { Radios } from "../../radios";
import { ExpandButton } from "../parts/expand_button";
import { TableData, TableProps } from "../table";
import { BulkSelect } from "./table_bulk_select";
import css from "./table_bulk_select.module.scss";

export type RowSelectionConditions<TABLE_DATA extends TableData> = {
  isRowSelectionDisabled?: (row: Row<TABLE_DATA>) => boolean;
  isBulkSelectionDisabled?: (info: HeaderContext<TABLE_DATA, TABLE_DATA>) => boolean;
  isPartiallySelected?: (info: HeaderContext<TABLE_DATA, TABLE_DATA>) => boolean;
  isAllSelected?: (info: HeaderContext<TABLE_DATA, TABLE_DATA>) => boolean;
  isRowExpandable?: (row: Row<TABLE_DATA>) => boolean;
};

export const generateSelectColumn = <TABLE_DATA extends TableData>(
  columnHelper: ColumnHelper<TABLE_DATA>,
  enableRowSelection: boolean,
  enableMultiRowSelection: boolean,
  enableNestedTable?: boolean,
  selectActionsAlignment?: TableProps<TABLE_DATA>["selectActionsAlignment"],
  {
    isRowSelectionDisabled = (row) => !row.getCanSelect(),
    isBulkSelectionDisabled,
    isPartiallySelected = (info) => !!info?.table?.getIsSomeRowsSelected(),
    isAllSelected = (info) => !!info?.table?.getIsAllRowsSelected(),
    isRowExpandable = () => false
  }: RowSelectionConditions<TABLE_DATA> = {}
) => {
  if (!enableNestedTable && !enableRowSelection) {
    return null;
  }
  return columnHelper.accessor((row) => row, {
    id: "select",
    enableSorting: false,
    header: (info) => (
      <Inline gap="spacingLg">
        {enableNestedTable && <div className={css.expandButtonPlaceholder} />}
        {enableRowSelection && enableMultiRowSelection && (
          <BulkSelect
            onChange={info?.table?.getToggleAllRowsSelectedHandler()}
            isAllSelected={isAllSelected(info)}
            isPartiallySelected={isPartiallySelected(info)}
            disabled={isBulkSelectionDisabled?.(info)}
          />
        )}
      </Inline>
    ),
    cell: ({ row }) => {
      const getSelectionComponent = () => {
        if (enableRowSelection && enableMultiRowSelection) {
          return (
            <Checkbox
              checked={row.getIsSelected()}
              onChange={(checked) => row.toggleSelected(checked)}
              disabled={isRowSelectionDisabled(row)}
              qaId={`RowSelect.${row.original?.id || row.id}`}
              className={css.checkboxRoot}
            />
          );
        } else if (enableRowSelection) {
          return (
            <Radios
              name="rowSelector"
              items={[
                {
                  id: row.id
                }
              ]}
              onChange={() => row.toggleSelected(true)}
              value={row.getIsSelected() ? row.id : undefined}
              classes={{
                root: css.radioRoot
              }}
            />
          );
        }
        return null;
      };

      return (
        <Stack justifyContent={selectActionsAlignment}>
          <Inline alignItems="start" className={css.selectActionsInlineContainer} gap="spacingLg">
            {enableNestedTable &&
              (isRowExpandable(row) ? (
                <ExpandButton
                  isOpen={row.getIsExpanded()}
                  onClick={(event) => {
                    event.stopPropagation();
                    row.toggleExpanded();
                  }}
                />
              ) : (
                <div className={css.expandButtonPlaceholder} />
              ))}
            {getSelectionComponent()}
          </Inline>
        </Stack>
      );
    },
    size: 32
  });
};
